<template>
  <detail-layout-page
    :title="title"
    :lead="lead"
    :is-sustainable="isSustainable"
    :body="body"
    :features="features"
    :nid-related="nid"
    :address="addressEntity"
    :zurich-card-benefit="zurichCardBenefit"
    :nid="nid"
    :key-visual="keyVisual"
    :images="mediaSlider"
    :review-url="reviewUrlReference"
  >
    <template v-if="languages.length" #languages>
      <div class="tour-languages">
        <div v-for="item in languages" :key="item.title">
          <div class="title mb-tiny text-size-6 tablet:text-size-5">
            <span>{{ item.title }}</span>
            <HelpBox>{{ item.description }}</HelpBox>
          </div>
          <div
            class="comma-list text-size-6 tablet:text-size-5"
            v-html="getItemList(item.languages)"
          />
        </div>
      </div>
    </template>

    <template v-if="ctaLinks.length" #cta>
      <DetailLayoutActionLinks :links="ctaLinks" />
    </template>

    <template
      v-if="prices?.length || priceSpecialInfo || priceDescription"
      #price
    >
      <table v-if="prices?.length" class="is-fullwidth detail-table table">
        <tbody>
          <tr v-for="item in filterNullArray(prices)" :key="item.label">
            <th>{{ item.label }}</th>
            <td class="text-right">
              {{ formatCurrency(item.value) }}
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="priceSpecialInfo" class="content" v-html="priceSpecialInfo" />
      <!-- Show price description if no TOMAS ID is given -->
      <div
        v-if="!tomasServiceId && priceDescription"
        class="content"
        v-html="priceDescription"
      />
    </template>

    <template v-if="remoteVideo" #video>
      <MediaRemoteVideo v-bind="remoteVideo" />
    </template>

    <template v-if="additionalInformationText" #additionalInformation>
      <div class="content" v-html="additionalInformationText" />
    </template>

    <template #timeAndDuration>
      <Weekdays
        v-if="weekdaysOpen.length"
        :open="weekdaysOpen"
        :title="$texts('availableDays')"
      />
      <table
        v-if="timeFormatted || duration || numberOfParticipants"
        class="is-fullwidth detail-table table"
      >
        <tbody>
          <tr v-if="timeFormatted">
            <th>{{ $texts('startTime') }}</th>
            <td><div class="content" v-html="timeFormatted" /></td>
          </tr>
          <tr v-if="duration">
            <th>{{ $texts('duration') }}</th>
            <td><div class="content" v-html="duration" /></td>
          </tr>
          <tr v-if="numberOfParticipants">
            <th>
              {{ $texts('numberOfParticipants') }}
            </th>
            <td>{{ numberOfParticipants }}</td>
          </tr>
        </tbody>
      </table>
    </template>

    <template v-if="addressStartpointEntity" #startingPoint>
      <AddressCard
        :address="addressStartpointEntity"
        no-travel-details
        :title="title"
      />
    </template>

    <template v-if="nid" #below>
      <TeaserQueryRelated v-if="showRelated" :nid="nid" />
    </template>
  </detail-layout-page>
</template>

<script lang="ts" setup>
import type { NodeTourFragment } from '#graphql-operations'
import { notNullish } from '@vueuse/core'
import type { ActionLink } from '~/components/DetailLayout'
import { filterNullArray } from '~/helpers'

const props = defineProps<NodeTourFragment>()

const { $texts } = useEasyTexts()
const currentLanguage = useCurrentLanguage()

const zurichCardBenefit = computed(() => props.zurichCard?.[0]?.name)

const languages = computed(() => {
  const publicLanguages = props.languagesPublic
    ?.map((v) => v?.label)
    .filter(notNullish)
  const group = props.languagesGroup?.map((v) => v?.label).filter(notNullish)
  const audio = props.languagesAudio?.map((v) => v?.label).filter(notNullish)

  return [
    {
      title: $texts('publicTour'),
      description: $texts('publicTourText'),
      languages: publicLanguages,
    },
    {
      title: $texts('privateTour'),
      description: $texts('privateTourText'),
      languages: group,
    },
    {
      title: $texts('audioGuide'),
      description: $texts('audioGuideText'),
      languages: audio,
    },
  ].filter((v) => v.languages?.length)
})

// Nuxt 2: computed: features()
const features = computed(() => {
  return props.details
    ?.map((text) => {
      if (text) {
        return {
          text,
        }
      }
    })
    ?.filter(notNullish)
})

// Nuxt 2: computed: ctaLinks()
const ctaLinks = computed<ActionLink[]>(() => {
  const links: ActionLink[] = [...(props.actionLinks || [])]
    .map((v) => {
      if (v) {
        return {
          title: v.title || '',
          url: v.url?.path || '',
          target: '_blank',
          classes: 'track-book-tour-unknown',
        }
      }
    })
    .filter(notNullish)

  if (showOnlineBooking.value) {
    links.unshift({
      title: $texts('bookPublicTour'),
      url: '/' + currentLanguage.value + '/book-public-tour/' + props.nid,
      target: '',
      classes: 'track-book-tour-public',
    })
  }

  if (props.webform) {
    const url = props.webform.url?.path
    if (url) {
      links.push({
        title: $texts('requestPrivateTour'),
        url: url + `?source_entity_id=${props.nid}&source_entity_type=node`,
        classes: 'track-book-tour-private',
      })
    }
  }

  return links
})

// Nuxt 2: computed: showOnlineBooking()
const showOnlineBooking = computed(() => {
  return props.showBookingWidget && !!props.tomasBookingId
})

// Nuxt 2: computed: addressEntity()
const addressEntity = computed(() => props.address)

// Nuxt 2: computed: addressStartpointEntity()
const addressStartpointEntity = computed(() => props.addressStartpoint)

// Nuxt 2: computed: additionalInformationText()
const additionalInformationText = computed(() => {
  let text = props.additionalInformation || ''

  if (props.isPartnerCompany) {
    text += `<p>${$texts('isPartnerCompany')}</p>`
  }

  return text
})

const timeFormatted = computed(() => {
  // remove any &nbsp; and whitespace from the time string
  return props.time
    ?.replace(/&nbsp;/g, '')
    .replace(/\s+/g, ' ')
    .trim()
})

// Nuxt 2: computed: numberOfParticipants()
const numberOfParticipants = computed(() => (props.participants || '').trim())

// Nuxt 2: computed: showRelated()
const showRelated = computed(() => true)

const weekdaysOpen = computed(
  () => props.weekdays?.map((v) => v?.tid).filter(notNullish) || [],
)

// Nuxt 2: computed: schemaOrg()
const schemaOrg = computed(() => {
  const address = props.address
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Trip',
        description: props.body,
        url: address?.website?.url?.path || undefined,
      },
    ],
  }
})

// Nuxt 2: methods
function formatCurrency(v: number): string {
  return 'CHF ' + v.toFixed(2)
}

function getItemList(v: Array<string | undefined> = []): string {
  return v.filter(notNullish).join(', ')
}

// Nuxt 2: head()
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: schemaOrg,
    },
  ],
})
</script>

<style lang="postcss">
.detail-table {
  th {
    @apply w-1/2;
  }
}

.tour-languages {
  @apply grid;
  grid-gap: 1.5rem;
  grid-template-columns: 1fr;
  @screen desktop {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
